import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, hlaskaZChyby } from '../api/client.js';
import type {
  Dodatek,
  Firma,
  Rekapitulace,
  HlidaceSouhrnZakazky,
  HlidaceZakazky,
  HlidacPolozka,
  DenikZaznam,
  NalezyOdpoved,
  Zakazka,
  ZmenaSeznam,
} from '../api/types.js';
import { nastavGlobalniZakazku, useVybranaZakazka } from '../lib/globalniZakazka.js';
import { useAuth } from '../auth/AuthContext.js';
import { formatDatum, formatDen, formatKc } from '../lib/format.js';
import { ZMENA_STAV_BARVA, ZMENA_STAV_LABEL } from '../lib/labels.js';
import {
  Banknote,
  Eye,
  ArrowRightLeft,
  BellRing,
  BookOpen,
  CheckCircle2,
  ChevronRight,
  FileSignature,
  Search,
} from 'lucide-react';
import {
  Badge,
  Btn,
  Callout,
  EmptyState,
  Nacitani,
  Napoveda,
  PageHeader,
  StatKarta,
} from '../components/ui.js';

/**
 * PŘEHLED — vstupní stránka kanceláře po loginu (UX audit B1.1 / TOP 1).
 * Jedna obrazovka „co dnes hoří": hlídače zakázky (alarmy/připomínky s akcí),
 * fronta nálezů (počet + nejstarší), rozpracované změny, nepodepsané deníky,
 * poslední dodatky. Zakázkově vázané části respektují globální přepínač
 * v hlavičce; fronta a dodatky jdou napříč (jsou to fronty práce).
 */
export function DashboardPage() {
  const { data: zakazky, isLoading: zakazkyNacita } = useQuery({
    queryKey: ['zakazky'],
    queryFn: () => api.get<Zakazka[]>('/api/projects'),
  });
  const { vybrana, vybranaId, nastav } = useVybranaZakazka(zakazky);
  const { maPravo } = useAuth();
  const maFinance = maPravo('finance.read');

  const { data: hlidaceSouhrn } = useQuery({
    queryKey: ['hlidace-souhrn'],
    queryFn: () => api.get<HlidaceSouhrnZakazky[]>('/api/zakazky/hlidace-souhrn'),
    enabled: maFinance,
    staleTime: 60 * 1000,
  });
  const { data: hlidace, isLoading: hlidaceNacita } = useQuery({
    queryKey: ['hlidace', vybranaId],
    queryFn: () => api.get<HlidaceZakazky>(`/api/zakazky/${vybranaId}/hlidace`),
    enabled: !!vybranaId,
  });
  const { data: fronta, isLoading: frontaNacita } = useQuery({
    queryKey: ['nalezy', 'stav=novy'],
    queryFn: () => api.get<NalezyOdpoved>('/api/nalezy?stav=novy'),
  });
  // Faktická čísla zakázky (žádné vymyšlené metriky): Σ schválených víceprací
  // z rekapitulace — hodnota, kterou vidí ředitel každý den ve SVÝCH datech.
  const { data: rekapitulace } = useQuery({
    queryKey: ['rekapitulace', vybranaId],
    queryFn: () => api.get<Rekapitulace>(`/api/zakazky/${vybranaId}/rekapitulace`),
    enabled: !!vybranaId && maFinance,
    staleTime: 60 * 1000,
  });
  const { data: zmeny, isLoading: zmenyNacita } = useQuery({
    queryKey: ['zmeny', `zakazkaId=${vybranaId}`],
    queryFn: () => api.get<ZmenaSeznam[]>(`/api/zmeny?zakazkaId=${vybranaId}`),
    enabled: !!vybranaId,
  });
  const { data: dny, isLoading: dnyNacita } = useQuery({
    queryKey: ['denik-dny', vybranaId],
    queryFn: () => api.get<DenikZaznam[]>(`/api/diary?zakazkaId=${vybranaId}`),
    enabled: !!vybranaId,
  });
  const { data: dodatky, isLoading: dodatkyNacita } = useQuery({
    queryKey: ['dodatky'],
    queryFn: () => api.get<Dodatek[]>('/api/dodatky'),
  });
  const { data: firma } = useQuery({
    queryKey: ['firma'],
    queryFn: () => api.get<Firma>('/api/tenants/me'),
    staleTime: 60 * 1000,
  });
  // Smlouvy jen kvůli checklistu rozjezdu (čerstvá firma bez SOD).
  const { data: smlouvy } = useQuery({
    queryKey: ['smlouvy'],
    queryFn: () => api.get<{ id: string }[]>('/api/contracts'),
    enabled: !!vybranaId,
    staleTime: 60 * 1000,
  });

  // Stav systému (Nielsen #1): dokud data nedorazí, NEUKAZOVAT „nic nehoří"
  // (jinak blikne falešný prázdný stav, než se přehled naplní).
  if (zakazkyNacita) {
    return (
      <>
        <PageHeader titulek="Přehled" />
        <Nacitani text="Načítám přehled…" />
      </>
    );
  }

  if (zakazky && zakazky.length === 0) {
    return (
      <>
        <PageHeader titulek="Přehled" />
        <EmptyState
          titulek="Zatím žádná zakázka"
          popis="Přehled se plní z dění na zakázkách. Založte první v sekci Zakázky."
          akce={
            <Link to="/zakazky" className="font-semibold text-primary underline underline-offset-2">
              Na Zakázky
            </Link>
          }
        />
      </>
    );
  }

  // Zakázky známe, ale detailní data přehledu se ještě načítají → spinner,
  // ne prázdný stav (Doherty/Visibility of status).
  if (hlidaceNacita || frontaNacita || zmenyNacita || dnyNacita || dodatkyNacita) {
    return (
      <>
        <PageHeader
          titulek="Přehled"
          kontext={vybrana ? `${vybrana.cislo} — ${vybrana.nazev}` : undefined}
        />
        <Nacitani text="Načítám přehled…" />
      </>
    );
  }

  const hlidaceOstatni = (hlidaceSouhrn ?? []).filter((z) => z.zakazka.id !== vybranaId);
  const rozjezdHotovy =
    (zakazky ?? []).length > 0 &&
    (smlouvy ?? []).length > 0 &&
    (firma?.pocetSoupisu ?? 0) > 0 &&
    (firma?.pocetUzivatelu ?? 0) > 1;
  const alarmy = hlidace?.alarmy ?? [];
  const pripominky = hlidace?.pripominky ?? [];
  const nove = fronta?.nalezy ?? [];
  // API řadí nejstarší nezpracované nahoru → první karta je nejstarší.
  const nejstarsi = nove[0];
  const rozpracovane = (zmeny ?? []).filter(
    (z) => z.marze == null && z.stav !== 'zamitnuta' && z.stav !== 'fakturovatelna',
  );
  const nepodepsane = (dny ?? []).filter((d) => d.nepodepsany);
  const posledniDodatky = (dodatky ?? []).slice(0, 3);

  const nicNehori =
    alarmy.length === 0 &&
    pripominky.length === 0 &&
    nove.length === 0 &&
    rozpracovane.length === 0 &&
    nepodepsane.length === 0;

  // Jeden dominantní pás „co teď hoří" (blueprint 16 §1): alarmy před
  // připomínkami, první je velký, zbytek za „…a N dalších". Nic z funkce nemizí.
  const hlidaceVse = [
    ...alarmy.map((h) => ({ h, ton: 'error' as const })),
    ...pripominky.map((h) => ({ h, ton: 'warning' as const })),
  ];
  const [prvni, ...dalsiHlidace] = hlidaceVse;

  // Sekundární dlaždice se vykreslí JEN když mají obsah (prázdné „0 …" pryč).
  const maFronta = nove.length > 0;
  const maZmeny = rozpracovane.length > 0;
  const maDeniky = nepodepsane.length > 0;
  const maDodatky = posledniDodatky.length > 0;
  const zadnaDlazdice = !maFronta && !maZmeny && !maDeniky && !maDodatky;

  return (
    <>
      <PageHeader
        titulek="Přehled"
        kontext={vybrana ? `${vybrana.cislo} — ${vybrana.nazev}` : undefined}
        akce={
          <Napoveda titulek="Jak to tu funguje">
            <p>
              <strong>Cesta peněz:</strong> nález z terénu → fronta → Změna (co fakturujeme ↑ / co
              nás to stojí ↓ / marže uprostřed) → podepsané ZL → dodatek → fakturace. Nahoře vidíte,
              co dnes hoří — každý alarm má akci.
            </p>
          </Napoveda>
        }
      />

      {/* VYŽADUJE POZORNOST — plnošířkový pás hned pod hlavičkou, od pracovní
          plochy oddělený popiskem a předělem (hoří především). Když nic nehoří,
          pás se nevykresluje — říká to empty-state níže. */}
      {vybrana?.cislo === 'UKÁZKA' && <PribehUkazky zmeny={zmeny ?? []} />}

      {!nicNehori && <PozornostPanel prvni={prvni} dalsi={dalsiHlidace} />}

      {/* Hoří i jinde? Aktuální zakázka má přednost, ale ostatní nesmí být
          neviditelné — jinak „vše v klidu" lže (důvěra především). */}
      {hlidaceOstatni.length > 0 && (
        <div className="rounded-lg border border-border border-l-4 border-l-warning bg-surface px-4 py-3">
          <p className="text-sm font-semibold">Na ostatních zakázkách:</p>
          <ul className="mt-1 flex flex-col gap-1">
            {hlidaceOstatni.map((z) => (
              <li key={z.zakazka.id} className="text-sm">
                <span className={z.alarmy.length > 0 ? 'font-semibold text-error-text' : ''}>
                  {z.zakazka.cislo} — {z.zakazka.nazev}:
                </span>{' '}
                {z.alarmy.length > 0 &&
                  `${z.alarmy.length} ${z.alarmy.length === 1 ? 'alarm' : z.alarmy.length < 5 ? 'alarmy' : 'alarmů'}`}
                {z.alarmy.length > 0 && z.pripominky.length > 0 && ', '}
                {z.pripominky.length > 0 &&
                  `${z.pripominky.length} ${z.pripominky.length === 1 ? 'připomínka' : z.pripominky.length < 5 ? 'připomínky' : 'připomínek'}`}{' '}
                <button
                  type="button"
                  className="font-semibold text-primary underline underline-offset-2"
                  onClick={() => nastav(z.zakazka.id)}
                >
                  přepnout →
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {!nicNehori && <hr className="border-border" />}
      {/* KPI řada (SaaS dashboard): čtyři čísla dne na jeden pohled; klik vede
          na příslušnou frontu. Popisky jsou <p> (ne heading — dlaždice nejsou
          sekce a nesmí kolidovat s kartami front níže). */}
      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <Link to="/nalezy" className="block">
          <StatKarta
            popisek="Nálezy ve frontě"
            hodnota={nove.length}
            detail={
              nejstarsi ? `nejstarší čeká ${nejstarsi.stariPracovnichDni} prac. dnů` : 'nic nečeká'
            }
            ton={nove.length > 0 ? 'warning' : 'neutral'}
            ikona={<Search size={18} strokeWidth={1.75} />}
          />
        </Link>
        <Link to="/zmeny" className="block">
          <StatKarta
            popisek="Rozpracované změny"
            hodnota={rozpracovane.length}
            detail="bez ocenění obou stran"
            ikona={<ArrowRightLeft size={18} strokeWidth={1.75} />}
          />
        </Link>
        <Link to="/denik" className="block">
          <StatKarta
            popisek="Nepodepsané deníky"
            hodnota={nepodepsane.length}
            detail={vybrana ? vybrana.nazev : undefined}
            ton={nepodepsane.length > 0 ? 'warning' : 'neutral'}
            ikona={<BookOpen size={18} strokeWidth={1.75} />}
          />
        </Link>
        <StatKarta
          popisek="Aktivní hlídače"
          hodnota={alarmy.length + pripominky.length}
          detail={alarmy.length > 0 ? `${alarmy.length} alarm` : 'žádný alarm'}
          ton={alarmy.length > 0 ? 'error' : 'neutral'}
          ikona={<BellRing size={18} strokeWidth={1.75} />}
        />
        {rekapitulace && rekapitulace.kumulativy.schvalene.pocet > 0 && (
          <Link to="/rekapitulace" className="block col-span-2 xl:col-span-4">
            <StatKarta
              popisek="Vícepráce schválené k fakturaci"
              hodnota={formatKc(rekapitulace.kumulativy.schvalene.vynos)}
              detail={`${rekapitulace.kumulativy.schvalene.pocet} ${sklonujZmeny(rekapitulace.kumulativy.schvalene.pocet)} · celá rekapitulace →`}
              ton="success"
              ikona={<Banknote size={18} strokeWidth={1.75} />}
            />
          </Link>
        )}
      </div>

      {/* Průvodce rozjezdem žije, dokud nejsou kroky hotové — nesmí zmizet
          v půlce jen proto, že vznikla první smlouva. */}
      {!rozjezdHotovy && (
        <ZacinameKarta
          maZakazku={(zakazky ?? []).length > 0}
          maSmlouvu={(smlouvy ?? []).length > 0}
          maSoupis={(firma?.pocetSoupisu ?? 0) > 0}
          maTym={(firma?.pocetUzivatelu ?? 0) > 1}
        />
      )}
      {nicNehori && rozjezdHotovy && (
        <EmptyState
          ikona={<CheckCircle2 size={22} strokeWidth={1.75} />}
          titulek="Dnes nic nehoří"
          popis="Fronta je prázdná, všechny změny mají obě strany a deníky jsou podepsané. Nálezy z terénu se objeví tady."
        />
      )}

      {/* Klidná věta místo prázdných dlaždic (blueprint 16 §1) — když nic nehoří,
          nicNehori už řeší empty-state; sem se dostaneme, když hoří hlídač, ale
          fronty/změny/deníky/dodatky jsou prázdné. */}
      {!nicNehori && zadnaDlazdice && <p className="text-text-secondary">Nic dalšího nečeká.</p>}

      {!zadnaDlazdice && (
        <div className="grid items-start gap-6 md:grid-cols-2">
          {/* Fronty práce — flat karty s vlastní barvou kategorie, řádky jsou
              celé klikací (Fitts), počet velký v hlavičce. */}
          {maFronta && (
            <FrontaKarta
              vzhled="nalezy"
              ikona={<Search size={20} strokeWidth={1.75} />}
              titulek="Nálezy"
              pocet={nove.length}
              podtitulek={
                nejstarsi ? `nejstarší čeká ${nejstarsi.stariPracovnichDni} prac. dnů` : undefined
              }
              odkaz="/nalezy"
              odkazText="všechny nálezy"
            >
              {nove.slice(0, 3).map((n) => (
                <FrontaRadek key={n.id} to="/nalezy">
                  <span className="min-w-0">
                    <span className="block truncate text-base">
                      „{n.popis.slice(0, 60)}
                      {n.popis.length > 60 ? '…' : ''}“
                    </span>
                    <span className="block text-sm text-text-secondary">
                      {n.autor.jmeno} · {formatDatum(n.kdy)}
                    </span>
                  </span>
                </FrontaRadek>
              ))}
            </FrontaKarta>
          )}

          {maZmeny && (
            <FrontaKarta
              vzhled="zmeny"
              ikona={<ArrowRightLeft size={20} strokeWidth={1.75} />}
              titulek="Rozpracované změny"
              pocet={rozpracovane.length}
              podtitulek="bez ocenění obou stran"
              odkaz="/zmeny"
              odkazText="všechny změny"
            >
              {rozpracovane.slice(0, 5).map((z) => (
                <FrontaRadek key={z.id} to={`/zmeny/${z.id}`}>
                  <span className="min-w-0 truncate text-base">
                    {z.cislo ?? z.id} — {z.popis}
                  </span>
                  <Badge barva={ZMENA_STAV_BARVA[z.stav]}>{ZMENA_STAV_LABEL[z.stav]}</Badge>
                </FrontaRadek>
              ))}
            </FrontaKarta>
          )}

          {maDeniky && (
            <FrontaKarta
              vzhled="deniky"
              ikona={<BookOpen size={20} strokeWidth={1.75} />}
              titulek="Nepodepsané deníky"
              pocet={nepodepsane.length}
              podtitulek={vybrana ? vybrana.nazev : undefined}
              odkaz="/denik"
              odkazText="deník"
            >
              {nepodepsane.slice(0, 5).map((d) => (
                <FrontaRadek key={d.id} to={`/denik/${d.id}`}>
                  <span className="min-w-0">
                    <span className="block text-base">{formatDen(d.datum)}</span>
                    {d.zapsal && (
                      <span className="block text-sm text-text-secondary">{d.zapsal.jmeno}</span>
                    )}
                  </span>
                </FrontaRadek>
              ))}
            </FrontaKarta>
          )}

          {maDodatky && (
            <FrontaKarta
              vzhled="dodatky"
              ikona={<FileSignature size={20} strokeWidth={1.75} />}
              titulek="Poslední dodatky"
              pocet={(dodatky ?? []).length}
              odkaz="/dodatky"
              odkazText="všechny dodatky"
            >
              {posledniDodatky.map((d) => (
                <FrontaRadek key={d.id} to={`/dodatky/${d.id}`}>
                  <span className="min-w-0 truncate text-base">
                    {d.cislo ?? d.id}
                    {d.smlouva ? ` — ${d.smlouva.protistrana}` : ''}
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    <span className="text-sm tabular-nums">{formatKc(d.castka)}</span>
                    <Badge stav={d.podepsano ? 'success' : 'info'}>
                      {d.podepsano ? 'podepsaný' : 'nepodepsaný'}
                    </Badge>
                  </span>
                </FrontaRadek>
              ))}
            </FrontaKarta>
          )}
        </div>
      )}
    </>
  );
}

/**
 * Fronta práce na Přehledu — flat karta s vlastní barvou kategorie: ikona
 * v barevném chipu, velký počet, klikací řádky a spodní řádek „všechny …".
 */
const FRONTA_VZHLED = {
  nalezy: { chip: 'bg-accent-tint text-accent-pressed', cislo: 'text-accent-pressed' },
  zmeny: { chip: 'bg-info-bg text-info-text', cislo: 'text-info-text' },
  deniky: { chip: 'bg-warning-bg text-warning-text', cislo: 'text-warning-text' },
  dodatky: { chip: 'bg-primary-tint text-primary', cislo: 'text-primary' },
} as const;

function FrontaKarta({
  vzhled,
  ikona,
  titulek,
  pocet,
  podtitulek,
  odkaz,
  odkazText,
  children,
}: {
  vzhled: keyof typeof FRONTA_VZHLED;
  ikona: React.ReactNode;
  titulek: string;
  pocet: number;
  podtitulek?: string;
  odkaz: string;
  odkazText: string;
  children: React.ReactNode;
}) {
  const v = FRONTA_VZHLED[vzhled];
  return (
    <section className="flex flex-col overflow-hidden rounded-xl border border-border bg-surface">
      <header className="flex items-center gap-3 px-4 py-3">
        <span className={`flex size-10 shrink-0 items-center justify-center rounded-lg ${v.chip}`}>
          {ikona}
        </span>
        <span className="min-w-0">
          <h3 className="text-base font-bold">{titulek}</h3>
          {podtitulek && <p className="truncate text-sm text-text-secondary">{podtitulek}</p>}
        </span>
        <span className={`ml-auto pl-2 text-3xl font-bold tabular-nums ${v.cislo}`}>{pocet}</span>
      </header>
      <div className="flex flex-col divide-y divide-border border-t border-border">{children}</div>
      <Link
        to={odkaz}
        className="flex min-h-11 items-center justify-end gap-1 border-t border-border px-4 text-sm font-semibold text-primary transition-colors duration-100 hover:bg-bg"
      >
        {odkazText}
        <ChevronRight size={16} strokeWidth={2} aria-hidden="true" />
      </Link>
    </section>
  );
}

/** Celý řádek fronty je klikací — obsah vlevo, šipka vpravo. */
function FrontaRadek({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="flex min-h-12 items-center justify-between gap-3 px-4 py-2.5 transition-colors duration-100 hover:bg-bg"
    >
      {children}
      <ChevronRight
        size={16}
        strokeWidth={2}
        aria-hidden="true"
        className="shrink-0 text-text-muted"
      />
    </Link>
  );
}

/**
 * Checklist rozjezdu — čerstvá firma po onboardingu nesmí skončit na prázdném
 * Přehledu bez dalšího kroku (zpětná vazba zadavatele). Kroky vedou cestou
 * peněz: zakázka → smlouva (SOD) → soupis prací → terén.
 */
function ZacinameKarta({
  maZakazku,
  maSmlouvu,
  maSoupis,
  maTym,
}: {
  maZakazku: boolean;
  maSmlouvu: boolean;
  maSoupis: boolean;
  maTym: boolean;
}) {
  const queryClient = useQueryClient();
  const [chybaUkazky, setChybaUkazky] = useState<string | null>(null);
  const ukazka = useMutation({
    mutationFn: () => api.post<{ zakazkaId: string }>('/api/tenants/ukazkova-stavba', {}),
    onSuccess: (r) => {
      nastavGlobalniZakazku(r.zakazkaId);
      void queryClient.invalidateQueries();
    },
    onError: (err) => setChybaUkazky(hlaskaZChyby(err)),
  });
  const kroky = [
    {
      hotovo: maZakazku,
      titulek: 'Založit zakázku',
      popis: 'Stavba, na které se pracuje.',
      odkaz: '/zakazky',
      odkazText: maZakazku ? 'zakázky' : 'založit',
    },
    {
      hotovo: maSmlouvu,
      titulek: 'Založit smlouvu s investorem (SOD)',
      popis: 'Základ cesty peněz — na smlouvu se váže ceník i dodatky.',
      odkaz: '/smlouvy/nova',
      odkazText: 'založit smlouvu',
    },
    {
      hotovo: maSoupis,
      titulek: 'Nahrát soupis prací ke smlouvě',
      popis: 'Z detailu smlouvy (sekce Ceník) — položky pak oceňují vícepráce.',
      odkaz: '/smlouvy',
      odkazText: 'smlouvy',
    },
    {
      hotovo: maTym,
      titulek: 'Předat mistrovi mobil',
      popis: 'Deník, fotky a nálezy píše terén — účty spravujete v Uživatelích.',
      odkaz: '/uzivatele',
      odkazText: 'uživatelé',
    },
  ];
  const prvniNehotovy = kroky.findIndex((k) => !k.hotovo);
  return (
    <section className="rounded-xl border border-border bg-surface">
      <header className="border-b border-border px-4 py-3">
        <h3 className="text-base font-bold">Začínáme — dokončete rozjezd stavby</h3>
        <p className="text-sm text-text-secondary">Až tudy projdete, Přehled se začne plnit sám.</p>
      </header>
      <ol className="flex flex-col divide-y divide-border">
        {kroky.map((k, i) => (
          <li key={k.titulek}>
            <Link
              to={k.odkaz}
              className={`flex min-h-12 items-center gap-3 px-4 py-2.5 transition-colors duration-100 hover:bg-bg ${
                i === prvniNehotovy ? '' : 'opacity-70'
              }`}
            >
              <span
                className={`flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                  k.hotovo
                    ? 'bg-success-bg text-success-text'
                    : i === prvniNehotovy
                      ? 'bg-accent text-text-heading'
                      : 'bg-fill-disabled text-text-secondary'
                }`}
              >
                {k.hotovo ? <CheckCircle2 size={14} strokeWidth={2.5} aria-hidden="true" /> : i + 1}
              </span>
              <span className="min-w-0 flex-1">
                <span className={`block text-base ${i === prvniNehotovy ? 'font-bold' : ''}`}>
                  {k.titulek}
                </span>
                <span className="block text-sm text-text-secondary">{k.popis}</span>
              </span>
              <span className="flex shrink-0 items-center gap-1 text-sm font-semibold text-primary">
                {k.odkazText}
                <ChevronRight size={14} strokeWidth={2} aria-hidden="true" />
              </span>
            </Link>
          </li>
        ))}
      </ol>
      {/* Nechce začínat naostro? Ukázková stavba je nejrychlejší „aha" moment
          — prohlédnout si appku naplněnou, než do ní sype vlastní data. */}
      <footer className="flex flex-wrap items-center gap-3 border-t border-border px-4 py-3">
        <Btn
          size="desktop"
          varianta="secondary"
          disabled={ukazka.isPending}
          onClick={() => ukazka.mutate()}
        >
          <Eye size={16} strokeWidth={2} aria-hidden="true" />
          {ukazka.isPending ? 'Připravuji ukázkovou stavbu…' : 'Prohlédnout ukázkovou stavbu'}
        </Btn>
        <span className="text-sm text-text-secondary">
          Vzorová data — uvidíte deník, nález i vícepráci naplněné.
        </span>
        {chybaUkazky && <p className="w-full text-sm text-error-text">{chybaUkazky}</p>}
      </footer>
    </section>
  );
}

/**
 * VYŽADUJE POZORNOST — hlídače (alarmy + připomínky) v odděleném sloupci,
 * ať se nemíchají s pracovními frontami. První (nejvážnější) rozbalený,
 * zbytek za „…a N dalších". Bez obsahu klidný řádek, ne prázdné místo.
 */
function PozornostPanel({
  prvni,
  dalsi,
}: {
  prvni?: { h: HlidacPolozka; ton: 'error' | 'warning' };
  dalsi: { h: HlidacPolozka; ton: 'error' | 'warning' }[];
}) {
  return (
    <aside aria-label="Vyžaduje pozornost" className="flex flex-col gap-3">
      <p className="text-xs font-semibold tracking-wider text-text-secondary uppercase">
        Vyžaduje pozornost{prvni ? ` (${1 + dalsi.length})` : ''}
      </p>
      {prvni ? (
        <>
          <Callout ton={prvni.ton}>
            <p className="text-base text-text-primary">{prvni.h.text}</p>
            <div className="mt-2">
              <HlidacOdkaz hlidac={prvni.h} />
            </div>
          </Callout>
          {dalsi.length > 0 && (
            <details className="rounded-lg border border-border bg-surface">
              <summary className="min-h-11 cursor-pointer list-none px-3 py-2 text-sm font-semibold text-text-secondary">
                …a {dalsi.length}{' '}
                {dalsi.length === 1 ? 'další' : dalsi.length < 5 ? 'další' : 'dalších'} [rozbalit]
              </summary>
              <div className="flex flex-col gap-2 p-2 pt-0">
                {dalsi.map(({ h, ton }, i) => (
                  <Callout key={`d${i}`} ton={ton}>
                    <p className="text-base text-text-primary">{h.text}</p>
                    <div className="mt-2">
                      <HlidacOdkaz hlidac={h} />
                    </div>
                  </Callout>
                ))}
              </div>
            </details>
          )}
        </>
      ) : (
        <p className="flex items-center gap-2 text-sm text-text-secondary">
          <CheckCircle2 size={16} strokeWidth={1.75} aria-hidden="true" className="text-success" />
          Žádný alarm ani připomínka.
        </p>
      )}
    </aside>
  );
}

/** Odkaz akce hlídače: na Změnu, na frontu, nebo (fallback) rekapitulaci. */
function HlidacOdkaz({ hlidac }: { hlidac: HlidacPolozka }) {
  if (hlidac.zmenaId) {
    return (
      <Link to={`/zmeny/${hlidac.zmenaId}`} className="font-semibold underline underline-offset-2">
        otevřít Změnu
      </Link>
    );
  }
  if (hlidac.nalezId) {
    return (
      <Link to="/nalezy" className="font-semibold underline underline-offset-2">
        otevřít frontu nálezů
      </Link>
    );
  }
  return null;
}

/**
 * Průvodce příběhem ukázkové stavby — prodejní argument, který si člověk
 * proklikne sám: fotka z terénu → nález → Změna s oběma stranami → marže.
 * Čísla odpovídají vzorovým datům (96 000 / 71 000), žádná vymyšlená tvrzení.
 */
function PribehUkazky({ zmeny }: { zmeny: ZmenaSeznam[] }) {
  const KLIC = 'helfr.pribehUkazkySkryt';
  const [skryto, setSkryto] = useState(() => {
    try {
      return localStorage.getItem(KLIC) === '1';
    } catch {
      return false;
    }
  });
  if (skryto) return null;
  const ukazkovaZmena = zmeny.find((z) => z.cislo === 'ZM-UKÁZKA');

  const kroky = [
    { text: 'Mistr zapsal den, přidal fotky a podepsal ho prstem.', to: '/denik' },
    { text: 'Při výkopu našel starou jímku — nález čeká ve frontě.', to: '/nalezy' },
    {
      text: 'Z nálezu je Změna: investorovi fakturujeme 96 000 Kč, nás stojí 71 000 Kč.',
      to: ukazkovaZmena ? `/zmeny/${ukazkovaZmena.id}` : '/zmeny',
    },
    { text: 'Rekapitulace ukáže marži — a investorovi odejde bez ní.', to: '/rekapitulace' },
  ];

  return (
    <section className="rounded-xl border border-accent bg-accent-tint p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-bold">Ukázková stavba — projděte si příběh jedné vícepráce</h2>
          <p className="text-sm text-text-secondary">
            Čtyři zastávky, pár minut. Přesně takhle potečou vaše skutečné zakázky.
          </p>
        </div>
        <button
          type="button"
          className="shrink-0 text-sm text-text-secondary underline underline-offset-2"
          onClick={() => {
            try {
              localStorage.setItem(KLIC, '1');
            } catch {
              /* private mode */
            }
            setSkryto(true);
          }}
        >
          skrýt
        </button>
      </div>
      <ol className="mt-3 grid gap-2 lg:grid-cols-4">
        {kroky.map((k, i) => (
          <li key={k.to}>
            <Link
              to={k.to}
              className="flex h-full min-h-14 items-start gap-2.5 rounded-lg border border-border bg-surface p-3 text-sm transition-colors duration-100 hover:border-accent"
            >
              <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-bold text-text-heading">
                {i + 1}
              </span>
              {k.text}
            </Link>
          </li>
        ))}
      </ol>
    </section>
  );
}

/** České skloňování počtu změn (shodné s Rekapitulací). */
function sklonujZmeny(n: number): string {
  if (n === 1) return 'změna';
  if (n >= 2 && n <= 4) return 'změny';
  return 'změn';
}
